/**
 * Signup funnel analytics.
 *
 * ## Why this is dual-written
 *
 * GA4 is blocked for visitors who decline cookies, which in South Africa is a
 * meaningful share of traffic. If funnel measurement depends on GA4 alone you
 * will systematically under-count exactly the audience you care about, and you
 * will never know it. So every funnel event goes to two places:
 *
 *   1. GA4 (`window.gtag`) — only when cookie consent has been granted.
 *   2. A first-party Firestore endpoint (`/api/analytics/funnel`) — always.
 *      This is an event log, not tracking: no cookies, no cross-site identifier,
 *      no IP stored. It is what you query to compare steps.
 *
 * `window.gtag` is declared here because the @next/third-parties GoogleAnalytics
 * component is what installs it at runtime, so the type has to be ambient.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/**
 * GA4 recommended event names where one exists, so the funnel shows up in
 * Google's own funnel reports without custom configuration.
 */
export const FUNNEL_EVENTS = {
  /** A visitor clicked a primary CTA on the homepage. */
  beginSignup: 'begin_signup',
  /** A vertical card was chosen on the selector page. */
  selectVertical: 'select_vertical',
  /** A monthly/annual/trial plan was chosen on a vertical landing page. */
  selectPlan: 'select_plan',
  /** A form step (or a single-scroll form, in the legacy case) came into view. */
  formStepView: 'form_step_view',
  /** A form step was completed and validated. */
  formStepComplete: 'form_step_complete',
  /** The long form was submitted and the profile was written. */
  formSubmit: 'form_submit',
  /** The visitor was handed off to PayFast. */
  paymentStart: 'payment_start',
  /** A validation error blocked progress. */
  formValidationError: 'form_validation_error',

  /**
   * A listing became publicly visible. This is the only event that represents
   * actual supply: everything before it is intent.
   */
  listingPublished: 'listing_published',

  /** A host completed the identity steps required to take payment. */
  verificationComplete: 'verification_complete',
  /** A WhatsApp CTA was tapped. */
  whatsappClick: 'whatsapp_click',
  /** A "search local businesses" style CTA was tapped. */
  directoryClick: 'directory_click',
} as const;

export type FunnelEventName = (typeof FUNNEL_EVENTS)[keyof typeof FUNNEL_EVENTS];

export interface FunnelEventParams {
  /** Registry vertical id, e.g. 'fixit'. */
  vertical?: string;
  /** Which button/tile produced the event. */
  cta?: string;
  /** 'trial' | 'monthly' | 'annual'. */
  billing?: string;
  /** 1-based step index inside a long form. */
  step?: number;
  total_steps?: number;
  /** Total form field count for the vertical. */
  field_count?: number;
  product_code?: string;
  price_zar?: number;
  currency?: string;
  /** Catch-all for event-specific detail. */
  [key: string]: string | number | boolean | undefined;
}

const FIRST_PARTY_ENDPOINT = '/api/analytics/funnel';

/**
 * Fire a funnel event. Never throws — analytics must not be able to break a
 * signup. Returns nothing; delivery is fire-and-forget.
 */
export function trackFunnelEvent(name: FunnelEventName, params: FunnelEventParams = {}): void {
  if (typeof window === 'undefined') return;

  const clean: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    clean[key] = value;
  }

  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, clean);
    }
  } catch {
    /* gtag unavailable or blocked by an extension — ignore */
  }

  try {
    const body = JSON.stringify({ name, params: clean, path: window.location.pathname });
    // keepalive so the event survives a navigation on the very next line.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(FIRST_PARTY_ENDPOINT, new Blob([body], { type: 'application/json' }));
    } else {
      void fetch(FIRST_PARTY_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* ignore */
  }
}
